import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  KIND_LABEL,
  KIND_LABEL_KEY,
  STATE_LABEL,
  STATE_LABEL_KEY,
  formatMinorAsString,
  useLockReceipt,
  useReceipt,
  useUnlockReceipt,
  type ReceiptDetail,
} from '@/hooks/use-receipts';
import { cn } from '@/lib/utils';
import { readFiscalBlock, readPrintedReceipt } from '@/lib/receipt-document';
import {
  FiscalBlockPanel,
  PrintedReceiptView,
  printedQrPayload,
} from '@/components/receipts/receipt-fiscal-view';
import { useHasPermission } from '@/auth/auth-store';
import { useT } from '@/i18n/use-t';

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; code?: string } | undefined;
    if (body?.message) return body.code ? `${body.code}: ${body.message}` : body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

export function ReceiptDetailPage() {
  const t = useT('admin_ui');
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useReceipt(id);
  const lock = useLockReceipt();
  const unlock = useUnlockReceipt();
  const [reason, setReason] = useState('');
  const [banner, setBanner] = useState<string | null>(null);
  // RFL-106: the server enforces `receipts.admin` on lock/unlock; hide
  // the controls when the user lacks it so they aren't offered an action
  // that would 403. The lock-state display stays visible (receipts.read).
  const canAdmin = useHasPermission('receipts.admin');

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  }
  if (!data) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('receipts.detail.notFound', 'Receipt not found.')}
      </p>
    );
  }

  async function runLock() {
    if (reason.trim().length === 0) {
      setBanner(t('receipts.detail.reasonRequired', 'Provide a reason before locking.'));
      return;
    }
    setBanner(null);
    try {
      await lock.mutateAsync({ id: id!, reason: reason.trim() });
      setReason('');
    } catch (err) {
      setBanner(extractError(err) ?? t('receipts.detail.lockFailed', 'Lock failed.'));
    }
  }

  async function runUnlock() {
    setBanner(null);
    try {
      await unlock.mutateAsync(id!);
    } catch (err) {
      setBanner(extractError(err) ?? t('receipts.detail.unlockFailed', 'Unlock failed.'));
    }
  }

  // PFC-21: what the till stored — shown only when it is there.
  const fiscalBlock = readFiscalBlock(data.fiscal, data.receiptDocument);
  const printed = readPrintedReceipt(data.receiptDocument);

  const isLocked = data.lifecycleState === 'locked';
  const canLock = data.lifecycleState === 'issued' || data.lifecycleState === 'partially_returned';

  // YUKI-39: render every amount in the receipt's OWN currency. The page

  // hardcoded a euro sign, so a Ukrainian retailer read hryvnia totals

  // labelled EUR and could not tell a label bug from a pricing one.

  const money = (minor: string): string =>
    `${formatMinorAsString(minor)}${data.currency === null ? '' : ` ${data.currency}`}`;

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <Link to="/receipts" className="text-xs text-muted-foreground hover:underline">
          {t('receipts.detail.back', '← Receipts')}
        </Link>
        <h1 className="text-2xl font-semibold">
          {t(KIND_LABEL_KEY[data.receiptType], KIND_LABEL[data.receiptType])}
        </h1>
        <div className="flex items-center gap-2 text-xs">
          <Badge
            variant={
              data.lifecycleState === 'locked'
                ? 'destructive'
                : data.lifecycleState === 'fully_returned'
                  ? 'success'
                  : 'default'
            }
          >
            {t(STATE_LABEL_KEY[data.lifecycleState], STATE_LABEL[data.lifecycleState])}
          </Badge>
          <span className="font-mono text-muted-foreground">{data.id}</span>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-4 rounded-md border border-border bg-card p-4 text-sm">
        <Row
          label={t('receipts.detail.issuedAt', 'Issued at')}
          value={new Date(data.finalisedAt).toLocaleString()}
        />
        <Row
          label={t('receipts.field.terminal', 'Terminal')}
          value={data.terminalLabel ?? data.issuingTerminalId}
        />
        <Row
          label={t('receipts.field.cashier', 'Cashier')}
          value={data.cashierDisplayName ?? data.cashierId ?? '—'}
        />
        {/*
          RL-513: the supervisor or second cashier who authorised this receipt
          for the cashier above — shown only when someone did.
        */}
        {(data.approverEmployeeCode ?? data.approverCashierId ?? null) !== null && (
          <Row label={t('receipts.detail.approvedBy', 'Approved by')} value={approverLabel(data)} />
        )}
        <Row label={t('receipts.field.total', 'Total')} value={money(data.totalMinor)} />
        <Row label={t('receipts.detail.subtotal', 'Subtotal')} value={money(data.subtotalMinor)} />
        <Row
          label={t('receipts.detail.discount', 'Discount')}
          value={data.totalDiscountMinor !== null ? money(data.totalDiscountMinor) : '—'}
        />
        {/*
          YUKI-39: the value a cashier scans to find this receipt for a return.
          Without it on screen there is no way to check, from Back Office, that
          an imported receipt is reachable from its printed QR.
        */}
        {data.legacyFiscalLink !== null && (
          <Row
            label={t('receipts.detail.fiscalLink', 'Fiscal link')}
            value={data.legacyFiscalLink}
            mono
          />
        )}
        {data.legacyDepartmentCode !== null && (
          <Row
            label={t('receipts.detail.legacyDepartment', 'Legacy department')}
            value={data.legacyDepartmentCode}
            mono
          />
        )}
        {data.originalReceiptId && (
          <Row
            label={t('receipts.detail.refundOf', 'Refund of')}
            value={data.originalReceiptId}
            mono
          />
        )}
        {data.lockedByTerminalId && (
          <Row
            label={t('receipts.detail.softLock', 'Soft-lock')}
            value={t('receipts.detail.softLockHeld', 'held by {terminal} until {until}')
              .replace('{terminal}', data.lockedByTerminalId)
              .replace(
                '{until}',
                data.lockedUntil ? new Date(data.lockedUntil).toLocaleString() : '—',
              )}
          />
        )}
      </section>

      {fiscalBlock !== null && (
        <FiscalBlockPanel
          block={fiscalBlock}
          showQrImage={
            printed === null || printedQrPayload(printed, fiscalBlock.qrPayload) === null
          }
        />
      )}

      <section className="rounded-md border border-border bg-card">
        <header className="border-b border-border px-4 py-2 text-sm font-medium">
          {t('receipts.detail.lines', 'Lines')}
        </header>
        <Table>
          <THead>
            <TR>
              <TH>{t('receipts.field.sku', 'SKU')}</TH>
              <TH>{t('receipts.field.name', 'Name')}</TH>
              <TH className="text-right">{t('receipts.field.qty', 'Qty')}</TH>
              <TH className="text-right">{t('receipts.detail.unit', 'Unit')}</TH>
              <TH className="text-right">{t('receipts.detail.lineTotal', 'Line total')}</TH>
              <TH>{t('receipts.detail.tax', 'Tax')}</TH>
            </TR>
          </THead>
          <TBody>
            {data.lines.map((l, i) => (
              <TR key={i}>
                <TD className="font-mono text-xs">{l.sku ?? '—'}</TD>
                <TD>
                  {l.name}
                  {l.exciseStampSkipped === true && (
                    <Badge variant="outline" className="ml-2" data-testid="excise-stamp-skipped">
                      {t('receipts.detail.exciseStampSkipped', 'Stamp skipped')}
                    </Badge>
                  )}
                </TD>
                <TD className="text-right font-mono text-xs">{l.quantity}</TD>
                <TD className="text-right font-mono text-xs">
                  {formatMinorAsString(l.unitPriceMinor)}
                </TD>
                <TD className="text-right font-mono text-xs">
                  {formatMinorAsString(l.lineTotalMinor)}
                </TD>
                <TD className="font-mono text-xs">
                  {l.taxCategory} ({l.taxRatePercent}%)
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </section>

      {printed !== null && (
        <PrintedReceiptView receipt={printed} qrPayload={fiscalBlock?.qrPayload ?? null} />
      )}

      {(data.payments ?? []).length > 0 && (
        <section className="rounded-md border border-border bg-card">
          <header className="border-b border-border px-4 py-2 text-sm font-medium">
            {t('receipts.detail.payments', 'Payments')}
          </header>
          <Table>
            <THead>
              <TR>
                <TH>{t('receipts.detail.payment.method', 'Method')}</TH>
                <TH className="text-right">{t('receipts.detail.payment.amount', 'Amount')}</TH>
                <TH>{t('receipts.detail.payment.card', 'Card')}</TH>
                <TH>{t('receipts.detail.payment.terminal', 'Terminal id')}</TH>
                <TH>RRN</TH>
                <TH>STAN</TH>
                <TH>{t('receipts.detail.payment.authCode', 'Authorisation code')}</TH>
                <TH>{t('receipts.detail.payment.status', 'Final status')}</TH>
              </TR>
            </THead>
            <TBody>
              {(data.payments ?? []).map((p, i) => {
                const host = p.extensions?.['snx.host'];
                const status = p.settlement?.status;
                return (
                  <TR key={i}>
                    <TD className="text-xs">{p.method}</TD>
                    <TD className="text-right font-mono text-xs">
                      {formatMinorAsString(p.amountMinor)}
                    </TD>
                    <TD className="font-mono text-xs">
                      {[host?.cardScheme, host?.maskedPAN ?? p.cardNumber]
                        .filter(Boolean)
                        .join(' ') || '—'}
                    </TD>
                    <TD className="font-mono text-xs">{p.paymentTerminalId ?? '—'}</TD>
                    <TD className="font-mono text-xs">{p.rrn ?? '—'}</TD>
                    <TD className="font-mono text-xs">{host?.stan ?? '—'}</TD>
                    <TD className="font-mono text-xs">{p.authorizationCode ?? '—'}</TD>
                    <TD className="text-xs">
                      {status === 'settled'
                        ? t('receipts.detail.payment.settled', 'Settled')
                        : status === 'voided'
                          ? t('receipts.detail.payment.voided', 'Voided')
                          : status === 'unresolved'
                            ? t('receipts.detail.payment.unresolved', 'Unresolved')
                            : host?.attemptId
                              ? t('receipts.detail.payment.notReported', 'Not reported yet')
                              : '—'}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </section>
      )}

      {data.returnAllocations.length > 0 && (
        <section className="rounded-md border border-border bg-card">
          <header className="border-b border-border px-4 py-2 text-sm font-medium">
            {t('receipts.detail.returnAllocations', 'Return allocations')}
          </header>
          <Table>
            <THead>
              <TR>
                <TH>{t('receipts.detail.lineIdx', 'Line idx')}</TH>
                <TH>{t('receipts.field.sku', 'SKU')}</TH>
                <TH className="text-right">{t('receipts.field.qty', 'Qty')}</TH>
                <TH className="text-right">{t('receipts.detail.refund', 'Refund')}</TH>
                <TH>{t('receipts.detail.refundReceipt', 'Refund receipt')}</TH>
                <TH>{t('receipts.field.when', 'When')}</TH>
              </TR>
            </THead>
            <TBody>
              {data.returnAllocations.map((a) => (
                <TR key={a.id}>
                  <TD className="font-mono text-xs">{a.originalLineIndex}</TD>
                  <TD className="font-mono text-xs">{a.originalLineSku}</TD>
                  <TD className="text-right font-mono text-xs">{a.quantityReturned}</TD>
                  <TD className="text-right font-mono text-xs">
                    {formatMinorAsString(a.refundAmountMinor)}
                  </TD>
                  <TD className="font-mono text-xs">{a.returnReceiptId.slice(-8)}</TD>
                  <TD className="font-mono text-xs">{new Date(a.createdAt).toLocaleString()}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </section>
      )}

      <section
        className={cn(
          'rounded-md border bg-card p-4',
          isLocked ? 'border-destructive/40 bg-destructive/5' : 'border-border',
        )}
      >
        <h2 className="text-sm font-medium">{t('receipts.detail.fraudHold', 'Fraud hold')}</h2>
        {/* Unlock restores `partially_returned` when return allocations exist,
            else `issued`. The operator sees the same status names as the badge
            above, never the raw lifecycle enum values (RL-331). */}
        <p className="mt-1 text-xs text-muted-foreground">
          {t(
            'receipts.detail.fraudHoldHelp',
            'Returns cannot be made against a locked receipt. Unlocking sets its status back to “{partial}” if items were already returned, otherwise to “{issued}”.',
          )
            .replace(
              '{partial}',
              t(STATE_LABEL_KEY.partially_returned, STATE_LABEL.partially_returned),
            )
            .replace('{issued}', t(STATE_LABEL_KEY.issued, STATE_LABEL.issued))}
        </p>
        {banner && (
          <p className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {banner}
          </p>
        )}
        {!canAdmin ? (
          <p className="mt-3 text-xs text-muted-foreground">
            {isLocked
              ? t('receipts.detail.statusLocked', 'Currently locked.')
              : t('receipts.detail.statusNotLocked', 'Currently not locked.')}{' '}
            {t('receipts.detail.adminRequiredPrefix', 'Locking and unlocking require the')}{' '}
            <code>receipts.admin</code> {t('receipts.detail.adminRequiredSuffix', 'permission.')}
          </p>
        ) : isLocked ? (
          <Button className="mt-3" onClick={runUnlock} disabled={unlock.isPending}>
            {unlock.isPending
              ? t('receipts.detail.unlocking', 'Unlocking…')
              : t('receipts.detail.unlock', 'Unlock')}
          </Button>
        ) : (
          <div className="mt-3 space-y-2">
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('receipts.detail.lockReasonPlaceholder', 'Lock reason (required)')}
              rows={2}
            />
            <Button onClick={runLock} disabled={!canLock || lock.isPending}>
              {lock.isPending
                ? t('receipts.detail.locking', 'Locking…')
                : t('receipts.detail.lock', 'Lock for fraud review')}
            </Button>
            {!canLock && (
              <p className="text-xs text-muted-foreground">
                {t('receipts.detail.cannotLock', 'Cannot lock a receipt in state')}{' '}
                <code>{data.lifecycleState}</code>.
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 shrink-0 text-muted-foreground">{label}</dt>
      <dd className={cn('flex-1 break-all', mono === true && 'font-mono text-xs')}>{value}</dd>
    </div>
  );
}

/** "Name (code)" when Back Office knows the approver, else whatever the till sent. */
function approverLabel(data: ReceiptDetail): string {
  const name = data.approverDisplayName ?? null;
  const code = data.approverEmployeeCode ?? null;
  if (name !== null && code !== null) return `${name} (${code})`;
  return name ?? code ?? data.approverCashierId ?? '—';
}
